import { z } from "zod";

// Schéma pour l'association utilisateur-organisation
export const UserOrganizationSchema = z.object({
  id: z.string().optional(),
  userId: z.string(), // ID de l'utilisateur Firebase
  organizationId: z.string(), // ID de l'organisation
  role: z.enum(["owner", "admin", "member"]), // Rôle dans l'organisation
  status: z.enum(["active", "pending", "suspended"]).default("active"),
  permissions: z
    .array(
      z.enum([
        "manage_orders",
        "manage_products",
        "manage_settings",
        "view_analytics",
        "manage_users",
        "manage_payments",
      ]),
    )
    .default([]),

  // Métadonnées
  joinedAt: z.any().optional(), // Timestamp
  lastActiveAt: z.any().optional(), // Timestamp
  invitedBy: z.string().optional(), // ID de l'utilisateur qui a invité
  invitedAt: z.any().optional(), // Timestamp d'invitation
});

// Schéma pour la création d'une organisation
export const CreateOrganizationSchema = z.object({
  name: z.string().min(2, "Le nom doit contenir au moins 2 caractères"),
  description: z.string().optional(),
  industry: z.string().optional(),
  website: z.string().url().optional().or(z.literal("")),
  phone: z.string().optional(),
  whatsappNumber: z.string().optional(),
  instagramPage: z.string().optional(),
  facebookPage: z.string().optional(),
  tiktokPage: z.string().optional(),
  address: z
    .object({
      street: z.string().optional(),
      city: z.string().optional(),
      postalCode: z.string().optional(),
      country: z.string().default("FR"),
    })
    .optional(),

  // Informations du créateur
  creatorEmail: z.string().email("Email invalide"),
});

// Schéma pour l'invitation d'un utilisateur
export const InviteUserSchema = z.object({
  email: z.string().email("Email invalide"),
  role: z.enum(["admin", "member"]).default("member"),
  permissions: z
    .array(
      z.enum([
        "manage_orders",
        "manage_products",
        "manage_settings",
        "view_analytics",
        "manage_users",
        "manage_payments",
      ]),
    )
    .default([]),
  message: z.string().optional(),
});

// Types TypeScript
export type UserOrganization = z.infer<typeof UserOrganizationSchema>;
export type CreateOrganization = z.infer<typeof CreateOrganizationSchema>;
export type InviteUser = z.infer<typeof InviteUserSchema>;

// Rôles et permissions prédéfinis
export const ORGANIZATION_ROLES = {
  owner: {
    name: "Propriétaire",
    description: "Accès complet à l'organisation",
    permissions: [
      "manage_orders",
      "manage_products",
      "manage_settings",
      "view_analytics",
      "manage_users",
      "manage_payments",
    ],
  },
  admin: {
    name: "Administrateur",
    description: "Gestion de l'organisation",
    permissions: [
      "manage_orders",
      "manage_products",
      "view_analytics",
      "manage_users",
    ],
  },
  member: {
    name: "Membre",
    description: "Accès de base",
    permissions: ["view_analytics"],
  },
} as const;

export const PERMISSION_DESCRIPTIONS = {
  manage_orders: "Gérer les commandes",
  manage_products: "Gérer les produits",
  manage_settings: "Gérer les paramètres",
  view_analytics: "Voir les statistiques",
  manage_users: "Gérer les utilisateurs",
  manage_payments: "Gérer les paiements",
} as const;
